import { ActualsValueTypes } from 'partner360/models/actualValueTypes'

import type { ICockpitKeyInitiative } from '../../model/types'

export type TInitiativeValueType = 'Goal' | 'Incremental'

/** Measures an initiative carries an amount for. Anything else (`Units`, `None`, …) has no
 *  column on the initiative, and the tile shows `--` rather than guessing one. */
export const SUPPORTED_INITIATIVE_METRICS = [
  ActualsValueTypes.Ndp,
  ActualsValueTypes.Net,
  ActualsValueTypes.TNet,
]

export const isSupportedInitiativeMetric = (metric: ActualsValueTypes): boolean =>
  SUPPORTED_INITIATIVE_METRICS.includes(metric)

/**
 * One initiative's goal or incremental amount in the selected measure and currency.
 *
 * Each measure comes in three columns — the initiative's own currency, USD and local currency.
 * The local-currency view reads its column only; the USD view falls back to the plain column,
 * which is what older initiatives carry.
 */
export const getInitiativeMetricAmount = (
  initiative: ICockpitKeyInitiative,
  valueType: TInitiativeValueType,
  metric: ActualsValueTypes,
  isLocalCurrency: boolean
): number | null => {
  let baseValue: number | undefined
  let usdValue: number | undefined
  let localCurrencyValue: number | undefined

  switch (metric) {
    case ActualsValueTypes.Ndp:
      baseValue = initiative[`${valueType}Ndp`]
      usdValue = initiative[`${valueType}NdpUSD`]
      localCurrencyValue = initiative[`${valueType}NdpLocalCurrency`]
      break
    case ActualsValueTypes.Net:
      baseValue = initiative[`${valueType}Net`]
      usdValue = initiative[`${valueType}NetUSD`]
      localCurrencyValue = initiative[`${valueType}NetLocalCurrency`]
      break
    case ActualsValueTypes.TNet:
      baseValue = initiative[`${valueType}TNet`]
      usdValue = initiative[`${valueType}TNetUSD`]
      localCurrencyValue = initiative[`${valueType}TNetLocalCurrency`]
      break
    default:
      return null
  }

  return isLocalCurrency ? (localCurrencyValue ?? null) : (usdValue ?? baseValue ?? null)
}

/** Reads the local MDF investment from the legacy field and USD from its dedicated field. */
export const getInitiativeMdfPlannedAmount = (
  initiative: ICockpitKeyInitiative,
  isLocalCurrency: boolean
): number => {
  if (initiative.HPInvestmentSource?.trim().toUpperCase() !== 'MDF') return 0

  const amount = isLocalCurrency ? initiative.HPInvestmentSum : initiative.HPInvestmentSumUSD

  return amount ?? 0
}
